import _ from 'lodash';
import type { FC } from 'react';
import { MultiSelect } from 'src/components/form/controlled-multiselect/ControlledMultiselect';
import type {
  HidableOption,
  HidableOptionGroup,
} from 'src/components/form/controlled-multiselect/types';
import UserSearchPreferences from 'src/components/user-search-preferences';

import type { CustomAttributeProps } from './CustomAttributeProps';

export const CustomAttributeUserMultiSelect: FC<
  CustomAttributeProps<null | string[]>
> = ({ label, onChange, value, disabled, error, description }) => {
  const getSelectedOptions = (
    options: (HidableOption | HidableOptionGroup)[],
    value: string[] | null
  ) => {
    const selectedOptions: (HidableOption | HidableOptionGroup)[] = [];
    for (const option of options) {
      if (option.value && value?.includes(option.value)) {
        selectedOptions.push(option);
      }
      if ('options' in option) {
        for (const subOption of option.options) {
          if (subOption.value && value?.includes(subOption.value)) {
            selectedOptions.push(subOption);
          }
        }
      }
    }

    return selectedOptions;
  };

  return (
    <UserSearchPreferences
      showInheritedContributorsToggle={false}
      includeGroups={true}
    >
      {({
        onBlur,
        onChange: handleUserChange,
        statusType,
        options,
        users,
        userGroups,
        preferencesButton,
      }) => (
        <MultiSelect
          statusType={statusType}
          sideControl={preferencesButton}
          testId={label}
          description={description}
          label={label}
          onBlur={onBlur}
          expandToViewport
          onChange={(e) => {
            handleUserChange(e.detail.selectedOptions);

            const selectedIds: string[] = [];
            for (const option of e.detail.selectedOptions) {
              if (
                'type' in option &&
                (option.type === 'user' || option.type === 'userGroup') &&
                option.value
              ) {
                selectedIds.push(option.value);
              }
            }

            onChange(selectedIds);
          }}
          options={options}
          disabled={disabled}
          placeholder={'Select'}
          errorMessage={error}
          tokenSection={<></>}
          selectedOptions={getSelectedOptions(options, value)}
          renderTokens={false}
          previewChangesFormatter={(val: string[] | null | undefined) => {
            if (Array.isArray(val)) {
              return val
                .map(
                  (v) =>
                    users?.user.find((u) => u.Id === v)?.FriendlyName ??
                    userGroups?.user_group.find((g) => g.Id === v)?.Name ??
                    v
                )
                .join(', ');
            }

            return '-';
          }}
          hasFieldChanged={(
            val:
              | {
                  from: string[] | null | undefined;
                  to: string[] | null | undefined;
                }
              | null
              | undefined
          ) => {
            if (val === undefined || val === null) {
              return false;
            }
            const from = [...(val.from ?? [])].sort();
            const to = [...(val.to ?? [])].sort();

            return !_.isEqual(from, to);
          }}
        />
      )}
    </UserSearchPreferences>
  );
};
